/**
 * Client for `POST /api/intents/update-customer-details` (tv-pzvy): staff fix a
 * customer's details from the record as ONE server transaction. The server
 * checks the fields (422 `domain-validation` naming the field), updates only the
 * changed columns and stamps the history line. Same fetch / auth / clock shape as
 * `@/api/pushIntents`: never throws; a failure carries `kind` (and `field`).
 */
import { authHeaders, isSigningIn, whenSignedIn } from '@/api/persist';
import { intentErrorKind, type IntentFailure } from '@/api/intents';
import { useSimulationStore } from '@/state/simulationStore';
import type { CustomerDetailField, CustomerDetailsHistoryEntry, CustomerDetailsInput } from '@atink/domain';

export type UpdateCustomerDetailsPayload = CustomerDetailsInput & { customerId: string; nowIso?: string };

export type UpdateCustomerDetailsResult =
  | { ok: true; unchanged: boolean; entry?: CustomerDetailsHistoryEntry }
  | (IntentFailure & { field?: CustomerDetailField });

export async function updateCustomerDetailsIntent(
  payload: UpdateCustomerDetailsPayload,
): Promise<UpdateCustomerDetailsResult> {
  if (isSigningIn()) await whenSignedIn();
  let res: Response;
  try {
    res = await fetch('/api/intents/update-customer-details', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ ...payload, nowIso: payload.nowIso ?? useSimulationStore.getState().clockIso }),
    });
  } catch {
    return { ok: false, kind: 'transient', status: 0, error: 'network' };
  }
  const body = (await res.json().catch(() => undefined)) as
    | { unchanged?: unknown; entry?: CustomerDetailsHistoryEntry; error?: string; code?: string; message?: string; field?: string }
    | undefined;
  if (res.ok) {
    return { ok: true, unchanged: body?.unchanged === true, ...(body?.entry ? { entry: body.entry } : {}) };
  }
  return {
    ok: false,
    kind: intentErrorKind(res.status, body),
    status: res.status,
    error: body?.error ?? `http-${res.status}`,
    ...(typeof body?.code === 'string' ? { code: body.code } : {}),
    ...(body?.message ? { message: body.message } : {}),
    ...(typeof body?.field === 'string' ? { field: body.field as CustomerDetailField } : {}),
  };
}
